import React from 'react';
import { Box, Button, Flex, Icon, Text } from '@chakra-ui/react';
import { FiChevronRight } from 'react-icons/fi';
import { Bounty, BountyClaim } from '@foodfluence/shared';
import { ff } from '../../../theme';
import { bountyMetaLine, claimStatusChip, isHighPaying, isNewBounty } from '../utils';
import BrandTile from './BrandTile';
import Pill from './Pill';
import { colorSchemeToTone } from '../theme';

type Props = {
  bounty: Bounty;
  claim?: BountyClaim;
  onClaim: (bounty: Bounty) => void;
  onViewClaim: (claim: BountyClaim) => void;
  isClaiming: boolean;
  /** True when this bounty's company matches one of the creator's worked-with brands. */
  isRecommended?: boolean;
  /** True when an admin has sent this creator a still-'sent' invite for this bounty. */
  isInvited?: boolean;
};

type Badge = { label: string; bg: string; color: string; boxShadow?: string };

const BountyCard: React.FC<Props> = ({ bounty, claim, onClaim, onViewClaim, isClaiming, isRecommended, isInvited }) => {
  const chip = claim ? claimStatusChip(claim.status) : null;
  const badges: Badge[] = ([
    isInvited && { label: 'Invited', bg: ff.lime, color: ff.ink },
    isRecommended && { label: 'Recommended', bg: '#F0F0EE', color: '#3E3E42' },
    isNewBounty(bounty, Date.now()) && { label: 'New', bg: ff.ink, color: ff.lime },
    isHighPaying(bounty) && { label: 'High Paying', bg: '#E4F3CC', color: '#2C5208' },
    bounty.visibility === 'private' && {
      label: 'Your Referral', bg: 'white', color: ff.ink, boxShadow: `inset 0 0 0 1.5px ${ff.ink}`,
    },
  ] as (Badge | false)[]).filter((b): b is Badge => Boolean(b));
  const meta = bountyMetaLine(bounty);

  return (
    <Box as="article" border="1px solid" borderColor="#E4E4E0" borderRadius="20px" p="16px">
      <Flex align="flex-start" gap="14px">
        <BrandTile name={bounty.companyName} logoUrl={bounty.logoUrl} />
        <Flex direction="column" gap="6px" flex={1} minW={0}>
          <Text textStyle="display" fontSize="24px" fontWeight={600} lineHeight={1} noOfLines={2} wordBreak="break-word">
            {bounty.companyName}
          </Text>
          {meta && <Text fontSize="13px" color={ff.text2}>{meta}</Text>}
          {badges.length > 0 && (
            <Flex wrap="wrap" gap="6px">
              {badges.map(b => (
                <Flex
                  as="span"
                  key={b.label}
                  h="24px"
                  px="8px"
                  align="center"
                  borderRadius="full"
                  fontSize="11px"
                  fontWeight={600}
                  letterSpacing="0.04em"
                  textTransform="uppercase"
                  whiteSpace="nowrap"
                  bg={b.bg}
                  color={b.color}
                  boxShadow={b.boxShadow}
                >
                  {b.label}
                </Flex>
              ))}
            </Flex>
          )}
        </Flex>
        <Flex direction="column" align="flex-end" gap="4px" pl="14px" borderLeft="1px solid" borderColor={ff.line} flex="none">
          <Text textStyle="display" fontSize="30px" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            ${bounty.amount ?? 0}
          </Text>
          <Text textStyle="eyebrow" fontSize="10px" letterSpacing="0.08em" color="#55555A">Payout</Text>
        </Flex>
      </Flex>

      {bounty.description && (
        <Text mt="14px" fontSize="14px" lineHeight="1.45" color="#3E3E42" noOfLines={2}>{bounty.description}</Text>
      )}

      {claim && chip ? (
        <Flex
          as="button"
          type="button"
          mt="14px"
          w="full"
          h="48px"
          pr="4px"
          align="center"
          justify="space-between"
          color={ff.ink}
          borderTop="1px solid"
          borderColor={ff.line}
          aria-label={`View your claim for ${bounty.companyName}`}
          onClick={() => onViewClaim(claim)}
        >
          <Pill label={chip.label} tone={colorSchemeToTone(chip.colorScheme)} />
          <Flex as="span" align="center" gap="4px" fontSize="14px" fontWeight={600}>
            View <Icon as={FiChevronRight} boxSize="16px" />
          </Flex>
        </Flex>
      ) : (
        <Button
          variant="lime"
          mt="14px"
          w="full"
          h="48px"
          borderRadius="full"
          fontSize="15px"
          isLoading={isClaiming}
          onClick={() => onClaim(bounty)}
        >
          Claim bounty
        </Button>
      )}
    </Box>
  );
};

export default BountyCard;
